import { Alert, Box, ExpandableSection, Grid, RadioGroup, SpaceBetween } from '@amzn/awsui-components-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EditEventActions, useEditEvent } from '../../../store/edit-event.context';
import { useUser } from '../../../store/user.context';
import { Event } from '../../../types/Event';
import { i18nKeys } from '../../../utils/i18n.utils';

import { RANKING_STRATEGIES, RankingStrategyType } from '../../../constants/shared/ranking-strategy';

interface RankingStrategyProps {
  target?: Event;
}

const RankingStrategy: React.FC<RankingStrategyProps> = ({ target }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { editMode, editedEvent, handleUpdateEditEvent } = useEditEvent();

  const rankingStrategy = target?.rankingStrategy ?? RANKING_STRATEGIES.CUMULATIVE_SOLVE_TIME;
  const editedRankingStrategy = editedEvent?.rankingStrategy ?? rankingStrategy;
  const isInProgress = !!target && target.isAfterStart && !target.ended;
  // Once an event is in progress, only jam-admins can change the ranking strategy to prevent
  // non-admins from recalculating the leaderboard mid-event.
  const canConfigure = !isInProgress || !!user?.isSuperAdmin;

  return (
    <ExpandableSection variant="container" headerText={t(i18nKeys.events.eventDetails.headers.rankingStrategy.header)}>
      <SpaceBetween direction="vertical" size="s">
        <Box>{t(i18nKeys.events.eventDetails.headers.rankingStrategy.description)}</Box>
        {editMode && isInProgress && canConfigure && (
          <Alert type="warning" statusIconAriaLabel={t(i18nKeys.general.warning)}>
            {t(i18nKeys.events.eventDetails.headers.rankingStrategy.inProgressWarning)}
          </Alert>
        )}
        {editMode && isInProgress && !canConfigure && (
          <Alert type="info" statusIconAriaLabel={t(i18nKeys.general.info)}>
            {t(i18nKeys.events.eventDetails.headers.rankingStrategy.inProgressAdminOnly)}
          </Alert>
        )}
        {editMode && user && canConfigure ? (
          <RadioGroup
            value={editedRankingStrategy}
            onChange={({ detail }) => handleUpdateEditEvent(EditEventActions.RANKING_STRATEGY, detail.value)}
            items={[
              {
                value: RANKING_STRATEGIES.CUMULATIVE_SOLVE_TIME,
                label: t(i18nKeys.events.eventDetails.labels.cumulativeSolveTime),
                description: t(i18nKeys.events.eventDetails.labels.cumulativeSolveTimeDescription),
              },
              {
                value: RANKING_STRATEGIES.MOST_RECENT_SCORE_TIME,
                label: t(i18nKeys.events.eventDetails.labels.mostRecentScoreTime),
                description: t(i18nKeys.events.eventDetails.labels.mostRecentScoreTimeDescription),
              },
            ]}
          />
        ) : (
          <div className="section-first-row">
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 6 }]}>
              <div className="secondary-text">{t(i18nKeys.events.eventDetails.labels.rankingStrategy)}</div>
              <div>
                {rankingStrategy === RANKING_STRATEGIES.MOST_RECENT_SCORE_TIME
                  ? t(i18nKeys.events.eventDetails.labels.mostRecentScoreTime)
                  : t(i18nKeys.events.eventDetails.labels.cumulativeSolveTime)}
              </div>
            </Grid>
          </div>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default RankingStrategy;
